import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL_ETL || 'http://localhost:8788';

export interface ChangeItem {
  id?: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  type: 'addition' | 'deduction' | 'modification';
  category: string;
  notes?: string;
}

export interface ChangeImpact {
  originalBudget: number;
  newBudget: number;
  impact: number;
  impactPercentage: number;
  itemsSummary: {
    additions: number;
    deductions: number;
    modifications: number;
    netChange: number;
  };
  categoryBreakdown?: Record<string, number>;
  recommendation?: 'approve' | 'reject' | 'review';
  riskLevel?: 'low' | 'medium' | 'high';
  timelineImpact?: number;
}

export interface ChangeOrderFull {
  id: string;
  projectId: string;
  number: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  status:
    | 'draft'
    | 'pending'
    | 'client_approved'
    | 'approved'
    | 'rejected'
    | 'executed';
  items: ChangeItem[];
  impact: ChangeImpact;
  requestedBy: string;
  requestedByName?: string;
  requestedAt?: number;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: number;
  rejectedReason?: string;
  executedAt?: number;
  attachments: string[];
  createdAt: number;
  updatedAt: number;
  metadata?: Record<string, any>;
  clientApprovedAt?: number | null;
  clientApprovedById?: string | null;
  clientApprovedByName?: string | null;
  clientSignatureSvg?: string | null;
  clientSignatureName?: string | null;
  clientRejectedAt?: number | null;
  clientRejectionReason?: string | null;
  notificationSentAt?: number | null;
}

export interface ChangeStats {
  total: number;
  pending: number;
  clientApproved: number;
  approved: number;
  rejected: number;
  executed: number;
  totalImpact: number;
  averageImpact: number;
}

export const changeManagementService = {
  calculateImpact: (items: ChangeItem[], originalBudget: number): ChangeImpact => {
    const totalAddition = items
      .filter((item) => item.type === 'addition')
      .reduce((sum, item) => sum + (item.quantity * item.unitPrice || item.total || 0), 0);
    const totalDeduction = items
      .filter((item) => item.type === 'deduction')
      .reduce((sum, item) => sum + (item.quantity * item.unitPrice || item.total || 0), 0);
    const totalModification = items
      .filter((item) => item.type === 'modification')
      .reduce((sum, item) => sum + (item.quantity * item.unitPrice || item.total || 0), 0);

    const netImpact = totalAddition - totalDeduction + totalModification;
    const newBudget = originalBudget + netImpact;
    const impactPercentage = originalBudget > 0 ? (netImpact / originalBudget) * 100 : 0;

    return {
      originalBudget,
      newBudget,
      impact: netImpact,
      impactPercentage,
      itemsSummary: {
        additions: totalAddition,
        deductions: totalDeduction,
        modifications: totalModification,
        netChange: netImpact,
      },
      riskLevel: Math.abs(impactPercentage) > 10 ? 'high' : 'medium',
      recommendation: Math.abs(impactPercentage) > 15 ? 'review' : 'approve',
    };
  },

  createChangeOrder: async (data: any): Promise<ChangeOrderFull> => {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/change-management`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  },

  getChangeOrders: async (projectId: string): Promise<ChangeOrderFull[]> => {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/change-management/project/${projectId}`
    );
    return response.data || [];
  },

  getChangeOrder: async (id: string): Promise<ChangeOrderFull> => {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/change-management/${id}`
    );
    return response.data;
  },

  updateChangeOrder: async (id: string, data: any): Promise<ChangeOrderFull> => {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/change-management/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return response.data;
  },

  deleteChangeOrder: async (id: string): Promise<void> => {
    await authService.fetchWithAuth(`${API_URL}/api/change-management/${id}`, {
      method: 'DELETE',
    });
  },

  approveChangeOrder: async (id: string): Promise<void> => {
    await authService.fetchWithAuth(
      `${API_URL}/api/change-management/${id}/approve`,
      { method: 'PUT' }
    );
  },

  rejectChangeOrder: async (id: string, reason: string): Promise<void> => {
    await authService.fetchWithAuth(
      `${API_URL}/api/change-management/${id}/reject`,
      {
        method: 'PUT',
        body: JSON.stringify({ reason }),
      }
    );
  },

  executeChangeOrder: async (id: string): Promise<void> => {
    await authService.fetchWithAuth(
      `${API_URL}/api/change-management/${id}/execute`,
      { method: 'PUT' }
    );
  },

  getChangeStats: async (projectId: string): Promise<ChangeStats> => {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/change-management/project/${projectId}/stats`
    );
    return response.data;
  },

  getChangeHistory: async (projectId: string): Promise<any[]> => {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/change-management/project/${projectId}/history`
    );
    return response.data || [];
  },
};